import React, { useEffect, useState, useMemo } from 'react';
import { Table, TableProps, Select, Row, Col, PaginationProps, Tag, Typography, Tooltip, Input, Space, Button } from 'antd';
import { SearchOutlined, CalendarOutlined, SyncOutlined, HistoryOutlined } from '@ant-design/icons';
import { getRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import { EllipsisTooltip } from './EllipsisTooltip';
import { ClassLevelSelect } from 'Components';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;

interface CancelledBillDataType {
  id: number;
  studentInvoiceId: number;
  billId?: string;
  invoidId?: string;
  studentId: number;
  studentCode: string;
  studentName: string;
  classYearId?: number;
  className?: string;
  classLevel?: string;
  cost: number;
  content: string;
  month: string;
  year: string;
  schoolYear: string;
  cancelledBy: string;
  cancelledDate: string | number;
  note?: string;
}

const formatCurrency = (value: number): string => {
  const formattedValue = new Intl.NumberFormat('vi-VN').format(value);
  return `${formattedValue} VNĐ`;
};

export const CancelledBillHistoryList: React.FC = () => {
  const [historyList, setHistoryList] = useState<CancelledBillDataType[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(() => getStoredWorkMonth().year);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
  const [classList, setClassList] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchStudentName, setSearchStudentName] = useState<string>('');
  const [debouncedStudentName, setDebouncedStudentName] = useState<string>('');

  const schoolId = localStorage.getItem('schoolId');

  const years = useMemo(() => {
    const startYear = 2021;
    const endYear = Math.max(new Date().getFullYear() + 2, selectedYear + 1);
    const list: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      list.push(y);
    }
    return list;
  }, [selectedYear]);

  const months = [
    { label: 'Tháng 1', value: 1 },
    { label: 'Tháng 2', value: 2 },
    { label: 'Tháng 3', value: 3 },
    { label: 'Tháng 4', value: 4 },
    { label: 'Tháng 5', value: 5 },
    { label: 'Tháng 6', value: 6 },
    { label: 'Tháng 7', value: 7 },
    { label: 'Tháng 8', value: 8 },
    { label: 'Tháng 9', value: 9 },
    { label: 'Tháng 10', value: 10 },
    { label: 'Tháng 11', value: 11 },
    { label: 'Tháng 12', value: 12 },
  ];

  const fetchHistory = async () => {
    setLoading(true);
    const queryParams: Record<string, string> = {
      page: currentPage.toString(),
      perPage: pageSize.toString(),
      month: selectedMonth.toString(),
      year: selectedYear.toString(),
      schoolId: schoolId || '',
    };
    if (selectedClass) {
      queryParams['classId'] = selectedClass.toString();
    } else if (selectedClassLevel) {
      queryParams['classLevel'] = selectedClassLevel;
    }
    if (debouncedStudentName) {
      queryParams['studentName'] = debouncedStudentName;
    }
    const queryString = new URLSearchParams(queryParams).toString();

    const url = `/cms/invoice/cancel-history?${queryString}`;
    const response: ResponseDataType<{ list: CancelledBillDataType[]; total: number }> = await getRequest(url);

    if (response?.code === 200) {
      setHistoryList(response.info?.list || []);
      setTotalItems(response.info?.total || 0);
    } else {
      setHistoryList([]);
      console.log(response?.msg || 'Lỗi khi tải lịch sử hủy biên lai');
    }
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStudentName(searchStudentName.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchStudentName]);

  const handlePressEnter = () => {
    setDebouncedStudentName(searchStudentName.trim());
    setCurrentPage(1);
  };

  // Lắng nghe thay đổi Kỳ làm việc từ cấu hình / thanh Header
  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      if (e.detail) {
        setSelectedMonth(e.detail.month);
        setSelectedYear(e.detail.year);
        setCurrentPage(1);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [selectedYear, selectedMonth, currentPage, pageSize, selectedClass, selectedClassLevel, debouncedStudentName]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolId) return;
      const url = `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${selectedYear}&page=1&perPage=1000`;
      const res: ResponseDataType<any> = await getRequest(url);
      if (res?.code === 200) {
        setClassList(res?.info?.list || []);
      }
    };
    fetchClasses();
  }, [schoolId, selectedYear]);

  const handlePageChange: PaginationProps['onChange'] = (page, pSize) => {
    setCurrentPage(page);
    setPageSize(pSize || 10);
  };

  const columns: TableProps<CancelledBillDataType>['columns'] = [
    {
      title: 'STT',
      key: 'stt',
      align: 'center',
      className: 'cell-center',
      width: 55,
      render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Mã biên lai',
      dataIndex: 'invoidId',
      key: 'invoidId',
      align: 'center',
      className: 'cell-center',
      width: 140,
      ellipsis: { showTitle: false },
      render: (_: any, record: CancelledBillDataType) => (
        <EllipsisTooltip text={record.invoidId || record.billId || String(record.studentInvoiceId)} />
      ),
    },
    {
      title: 'CCCD / Mã HS',
      dataIndex: 'studentCode',
      key: 'studentCode',
      align: 'center',
      className: 'cell-center',
      width: 130,
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Tên học sinh',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'left',
      width: 180,
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Lớp học',
      dataIndex: 'className',
      key: 'className',
      align: 'center',
      className: 'cell-center',
      width: 110,
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text || '—'} />,
    },
    {
      title: 'Khoản thu',
      dataIndex: 'content',
      key: 'content',
      align: 'left',
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Số tiền lúc hủy',
      dataIndex: 'cost',
      key: 'cost',
      align: 'center',
      className: 'cell-center',
      width: 135,
      render: (cost: number) => (
        <Text strong style={{ color: '#cf1322' }}>
          {formatCurrency(cost)}
        </Text>
      ),
    },
    {
      title: 'Người hủy',
      dataIndex: 'cancelledBy',
      key: 'cancelledBy',
      align: 'center',
      className: 'cell-center',
      width: 130,
      ellipsis: { showTitle: false },
      render: (by: string) => <Tag color="orange">{by || 'system'}</Tag>,
    },
    {
      title: 'Thời gian hủy',
      dataIndex: 'cancelledDate',
      key: 'cancelledDate',
      align: 'center',
      className: 'cell-center',
      width: 155,
      render: (val: any) => {
        if (!val) return '—';
        const formatted = dayjs(val).format('HH:mm DD/MM/YYYY');
        return <span style={{ fontSize: 13 }}>{formatted}</span>;
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      align: 'center',
      className: 'cell-center',
      width: 100,
      render: () => <Tag color="error">Đã hủy</Tag>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            placeholder="Năm"
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
            style={{ width: 100 }}
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Tháng"
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            style={{ width: 110 }}
          >
            {months.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </Select>
          <ClassLevelSelect
            value={selectedClassLevel}
            onChange={(level) => {
              setSelectedClassLevel(level);
              setSelectedClass(undefined);
            }}
          />
          <Select
            placeholder="Lớp"
            value={selectedClass}
            onChange={(value) => setSelectedClass(value)}
            allowClear
            style={{ width: 140 }}
          >
            {classList
              .filter((c) => !selectedClassLevel || String(c.classLevel).trim() === selectedClassLevel.trim())
              .map((c) => (
                <Option key={c.classYearId} value={c.classYearId}>
                  {c.className}
                </Option>
              ))}
          </Select>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            placeholder="Tìm kiếm tên HS, CCCD, Mã bill..."
            value={searchStudentName}
            onChange={(e) => setSearchStudentName(e.target.value)}
            onPressEnter={handlePressEnter}
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            allowClear
          />
          <Tooltip title="Tải lại dữ liệu">
            <Button icon={<SyncOutlined spin={loading} />} onClick={fetchHistory} />
          </Tooltip>
        </div>
      </div>

      <Table
        dataSource={historyList}
        columns={columns}
        rowKey="id"
        loading={loading}
        bordered
        size="middle"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: handlePageChange,
          showTotal: (total) => `Tổng cộng ${total} biên lai đã hủy`,
        }}
      />
    </div>
  );
};
